import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  AsyncRequestStatus,
  requestStatusFailure,
  requestStatusInProgress,
  requestStatusSuccess
} from '@portal/shared/store/request-status';
import { GroupStatus } from 'src/app/pages/administration/report-groups/report-groups.types';
import { EquipmentListingFieldName } from '../../devices/components/devices.models';
import { HealthFilterName, HealthFilterPayload } from '../health-api.types';
import { HealthMonitorActions } from './health-monitor.actions';
import { HealthMonitorDeviceFilterUtil, HealthMonitorWidgetDeviceFilter } from './health-monitor-device-filter-util';
import {
  HealthFilterModel,
  initialAlarmStatistics,
  initialBatteryStatistic,
  initialDeviceTypesFilters,
  initialHealthStatuses,
  initialReadRateStatistic,
  initialState
} from './health-monitor.state';

function getFilterByTypeAndFilteredArray(
  healthMonitorFiltersArray: HealthFilterModel[],
  filterType: EquipmentListingFieldName
): { filtersArrayWithoutCurrentFilter: HealthFilterModel[]; currentFilter: HealthFilterModel | undefined } {
  const currentFilter = healthMonitorFiltersArray.find(f => f.filterType === filterType);
  const filtersArrayWithoutCurrentFilter = healthMonitorFiltersArray.filter(f => f.filterType !== filterType);
  return { filtersArrayWithoutCurrentFilter, currentFilter };
}

const healthMonitorReducer = createReducer(
  initialState,
  on(HealthMonitorActions.getPersistedFiltersState, state => ({
    ...state,
    filtersAndInitFiltersStateRequest: requestStatusInProgress()
  })),
  on(HealthMonitorActions.getFiltersAndInitFiltersStateSuccess, (state, { reportGroups, filtersState }) => {
    const reportGroupItems = reportGroups.map(({ groupName, groupUID, groupStatus }) => ({
      text: groupName,
      value: groupUID,
      isDisabled: groupStatus === GroupStatus.Calculating
    }));
    const { filtersArrayWithoutCurrentFilter: healthMonitorFilters, currentFilter: reportGroupFilter } =
      getFilterByTypeAndFilteredArray(state.healthMonitorFilters, HealthFilterName.ReportGroup);
    const persistedReportGroupIds = filtersState?.reportGroupIds;
    const selectedReportGroupItems = persistedReportGroupIds?.length
      ? reportGroupItems.filter(item => persistedReportGroupIds.includes(item.value))
      : [];

    return {
      ...state,
      filtersAndInitFiltersStateRequest: requestStatusSuccess(),
      healthMonitorFilters: [
        ...healthMonitorFilters,
        {
          ...reportGroupFilter,
          items: reportGroupItems,
          defaultItems: reportGroupItems,
          selected: selectedReportGroupItems.length ? selectedReportGroupItems : reportGroupItems
        }
      ] as HealthFilterModel[],
      selectedDevicesFilters: filtersState?.selectedDeviceTypes ?? [],
      alarmsDateRange: filtersState?.alarmsDateRange ?? null,
      historyLogDateRange: filtersState?.historyLogDateRange ?? null
    };
  }),
  on(HealthMonitorActions.getFiltersAndInitFiltersStateFailure, (state, { error: _error }) => ({
    ...state,
    filtersAndInitFiltersStateRequest: requestStatusSuccess(),
    healthMonitorFilters: state.healthMonitorFilters.map(filter => ({
      ...filter,
      items: filter.defaultItems,
      selected: filter.defaultItems
    }))
  })),

  on(HealthMonitorActions.updateFilter, (state, { filterType, items }) => {
    const { filtersArrayWithoutCurrentFilter, currentFilter } = getFilterByTypeAndFilteredArray(
      state.healthMonitorFilters,
      filterType
    );
    return {
      ...state,
      healthMonitorFilters: [
        ...filtersArrayWithoutCurrentFilter,
        currentFilter && { ...currentFilter, selected: items }
      ] as HealthFilterModel[]
    };
  }),
  on(HealthMonitorActions.resetFilter, state => ({
    ...state,
    healthMonitorFilters: state.healthMonitorFilters.map(filter => ({
      ...filter,
      selected: [...filter.defaultItems]
    }))
  })),

  on(HealthMonitorActions.getFiltersCount, HealthMonitorActions.updateFiltersCount, state => ({
    ...state,
    filtersCountRequest: requestStatusInProgress()
  })),
  on(HealthMonitorActions.getFiltersCountSuccess, (state, { filters }) => ({
    ...state,
    deviceTypesFilters: filters,
    filtersCountRequest: requestStatusSuccess(),
    readRateStatisticRequest: requestStatusInProgress(),
    readSuccessRateStatisticRequest: requestStatusInProgress(),
    batteryStatisticRequest: requestStatusInProgress(),
    healthStatusesRequest: requestStatusInProgress()
  })),
  on(HealthMonitorActions.getFiltersCountFailure, (state, { error }) => ({
    ...state,
    deviceTypesFilters: initialDeviceTypesFilters,
    filtersCountRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.updateChips, (state, { chip: { value: filterType } }) => ({
    ...state,
    selectedDevicesFilters: state.selectedDevicesFilters.includes(filterType)
      ? state.selectedDevicesFilters.filter(value => value !== filterType)
      : [...state.selectedDevicesFilters, filterType]
  })),
  on(HealthMonitorActions.clearChips, state => ({ ...state, selectedDevicesFilters: [] })),

  on(HealthMonitorActions.getReadRateStatisticSuccess, (state, { readRateStatistic }) => ({
    ...state,
    readRateStatistic,
    readRateStatisticRequest: requestStatusSuccess()
  })),
  on(HealthMonitorActions.getReadRateStatisticFailure, (state, { error }) => ({
    ...state,
    readRateStatisticRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.getReadSuccessRateStatisticSuccess, (state, { readSuccessRateStatistic }) => ({
    ...state,
    readSuccessRateStatistic,
    readSuccessRateStatisticRequest: requestStatusSuccess()
  })),
  on(HealthMonitorActions.getReadSuccessRateStatisticFailure, (state, { error }) => ({
    ...state,
    readSuccessRateStatisticRequest: requestStatusFailure(error)
  })),

  on(
    HealthMonitorActions.getHealthBatteryStatisticSuccess,
    (state, { batteryStatistic, batteryDevicesIsNotEmpty }) => ({
      ...state,
      batteryStatistic,
      batteryDevicesIsNotEmpty,
      batteryStatisticRequest: requestStatusSuccess()
    })
  ),
  on(HealthMonitorActions.getHealthBatteryStatisticFailure, (state, { error }) => ({
    ...state,
    batteryStatistic: initialBatteryStatistic,
    batteryStatisticRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.getHealthStatusesSuccess, (state, { healthStatuses }) => ({
    ...state,
    healthStatuses,
    healthStatusesRequest: requestStatusSuccess()
  })),
  on(HealthMonitorActions.getHealthStatusesFailure, (state, { error }) => ({
    ...state,
    healthStatuses: initialHealthStatuses,
    healthStatusesRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.getHistoryLog, state => ({
    ...state,
    historyLogRequest: requestStatusInProgress()
  })),
  on(HealthMonitorActions.getHistoryLogSuccess, (state, { historyLog }) => ({
    ...state,
    historyLog,
    historyLogRequest: requestStatusSuccess()
  })),
  on(HealthMonitorActions.getHistoryLogFailure, (state, { error }) => ({
    ...state,
    historyLog: [],
    historyLogRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.getAlarmStatistics, state => ({
    ...state,
    alarmStatisticsRequest: requestStatusInProgress()
  })),
  on(HealthMonitorActions.getAlarmStatisticsSuccess, (state, { alarmStatistics }) => ({
    ...state,
    alarmStatistics,
    alarmStatisticsRequest: requestStatusSuccess()
  })),
  on(HealthMonitorActions.getAlarmStatisticsFailure, (state, { error }) => ({
    ...state,
    alarmStatistics: initialAlarmStatistics,
    alarmStatisticsRequest: requestStatusFailure(error)
  })),

  on(HealthMonitorActions.dcuFilterActivate, state => ({
    ...state,
    readRateStatistic: initialReadRateStatistic
  })),
  on(HealthMonitorActions.resetState, () => ({ ...initialState })),
  on(HealthMonitorActions.updateAlarmsDateRange, (state, { alarmsDateRange }) => ({
    ...state,
    alarmsDateRange
  })),
  on(HealthMonitorActions.updateHistoryDateRange, (state, { historyLogDateRange }) => ({
    ...state,
    historyLogDateRange
  }))
);

export const healthMonitorFeature = createFeature({
  name: 'healthMonitor',
  reducer: healthMonitorReducer,
  extraSelectors: ({
    selectDeviceTypesFilters,
    selectSelectedDevicesFilters,
    selectHealthMonitorFilters,
    selectFiltersCountRequest,
    selectFiltersAndInitFiltersStateRequest
  }) => ({
    selectDeviceTypesFiltersLoaded: createSelector(
      selectFiltersCountRequest,
      req => req.status === AsyncRequestStatus.Success
    ),
    selectFiltersLoaded: createSelector(
      selectFiltersAndInitFiltersStateRequest,
      req => req.status === AsyncRequestStatus.Success
    ),
    selectIsSingleDeviceTypeAvailable: createSelector(selectDeviceTypesFilters, deviceFilters => {
      let zeroCounter = 0;
      const filtersDisabledQuantity = 2;
      deviceFilters.forEach(filter => {
        if (filter.count === 0) zeroCounter++;
      });
      return zeroCounter === filtersDisabledQuantity;
    }),
    selectIsSingleDeviceTypeSelected: createSelector(
      selectSelectedDevicesFilters,
      deviceFilters => deviceFilters.length === 1
    ),
    selectIsAnySingleDevice: createSelector(
      createSelector(selectDeviceTypesFilters, deviceFilters => {
        let zeroCounter = 0;
        deviceFilters.forEach(f => {
          if (f.count === 0) zeroCounter++;
        });
        return zeroCounter === 2;
      }),
      createSelector(selectSelectedDevicesFilters, filters => filters.length === 1),
      (isSingleAvailable, isFilterSelected) => (isSingleAvailable ? isSingleAvailable : isFilterSelected)
    ),
    selectAlarmsStatisticsWidgetFilterPayload: createSelector(selectSelectedDevicesFilters, selectedDevicesFilters =>
      HealthMonitorDeviceFilterUtil.preparePayload(
        HealthMonitorWidgetDeviceFilter.AlarmsStatistics,
        selectedDevicesFilters
      )
    ),
    selectHealthReadRateWidgetFilterPayload: createSelector(selectSelectedDevicesFilters, selectedDevicesFilters =>
      HealthMonitorDeviceFilterUtil.preparePayload(
        HealthMonitorWidgetDeviceFilter.HealthReadRate,
        selectedDevicesFilters
      )
    ),
    selectReadSuccessRateWidgetFilterPayload: createSelector(selectSelectedDevicesFilters, selectedDevicesFilters =>
      HealthMonitorDeviceFilterUtil.preparePayload(
        HealthMonitorWidgetDeviceFilter.ReadSuccessRate,
        selectedDevicesFilters
      )
    ),
    selectBatteryCapacityWidgetFilterPayload: createSelector(selectSelectedDevicesFilters, selectedDevicesFilters =>
      HealthMonitorDeviceFilterUtil.preparePayload(
        HealthMonitorWidgetDeviceFilter.BatteryCapacity,
        selectedDevicesFilters
      )
    ),
    selectOtherWidgetFilterPayload: createSelector(selectSelectedDevicesFilters, selectedDevicesFilters =>
      HealthMonitorDeviceFilterUtil.preparePayload(HealthMonitorWidgetDeviceFilter.Other, selectedDevicesFilters)
    ),
    selectHealthMonitorFiltersPayload: createSelector(
      selectHealthMonitorFilters,
      filters =>
        filters
          .filter(filter => filter.selected.length > 0 && filter.selected.length !== filter.defaultItems.length)
          .map(filter => ({
            filterType: filter.filterType,
            filteredValues: filter.selected.map(s => s.value)
          })) as HealthFilterPayload[]
    ),
    selectHealthMonitorReportGroupValues: createSelector(selectHealthMonitorFilters, filters =>
      filters.find(filter => filter.filterType === HealthFilterName.ReportGroup)?.selected.map(item => item.value)
    )
  })
});

export const {
  selectHealthMonitorFilters,
  selectDeviceTypesFilters,
  selectDeviceTypesFiltersLoaded,
  selectFiltersCountRequest,
  selectSelectedDevicesFilters,
  selectReadRateStatistic,
  selectReadRateStatisticRequest,
  selectReadSuccessRateStatistic,
  selectReadSuccessRateStatisticRequest,
  selectBatteryStatistic,
  selectBatteryDevicesIsNotEmpty,
  selectBatteryStatisticRequest,
  selectHealthStatuses,
  selectHealthStatusesRequest,
  selectHistoryLog,
  selectHistoryLogRequest,
  selectAlarmStatistics,
  selectAlarmStatisticsRequest,
  selectFiltersLoaded,
  selectFiltersAndInitFiltersStateRequest,
  selectAlarmsDateRange,
  selectHistoryLogDateRange,
  selectIsSingleDeviceTypeAvailable,
  selectIsSingleDeviceTypeSelected,
  selectIsAnySingleDevice,
  selectAlarmsStatisticsWidgetFilterPayload,
  selectHealthReadRateWidgetFilterPayload,
  selectReadSuccessRateWidgetFilterPayload,
  selectBatteryCapacityWidgetFilterPayload,
  selectOtherWidgetFilterPayload,
  selectHealthMonitorFiltersPayload,
  selectHealthMonitorReportGroupValues
} = healthMonitorFeature;
